const STORAGE_KEYS = [
  "fbclid",
  "utm_source",
  "utm_campaign",
  "utm_medium",
  "utm_content",
];

const ONELINK = "https://montiway.onelink.me/9u0F";

const emptyAttribution = () => ({
  fbclid: "",
  utm_source: "",
  utm_campaign: "",
  utm_medium: "",
  utm_content: "",
});

const searchParamsFrom = (search) => {
  const sources = [];
  if (typeof search === "string") sources.push(new URLSearchParams(search));
  if (typeof window !== "undefined") {
    sources.push(new URLSearchParams(window.location.search));
  }
  return sources;
};

// Saved params are dropped this long after the visit that brought them.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const SAVED_AT_KEY = "attribution_saved_at";
// utm_source of our own internal links (e.g. /quiz/?utm_source=site&...):
// those must not replace the ad's params.
const INTERNAL_SOURCE = "site";

const readStored = () =>
  STORAGE_KEYS.reduce((stored, key) => {
    stored[key] = localStorage.getItem(key) || "";
    return stored;
  }, emptyAttribution());

const clearStored = () => {
  STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem(SAVED_AT_KEY);
};

// Values without a timestamp (saved before the expiry existed) count as expired.
const clearIfExpired = () => {
  if (!hasAttribution(readStored())) return;
  const savedAt = Number(localStorage.getItem(SAVED_AT_KEY));
  if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) clearStored();
};

/**
 * Saves fbclid / utm_* from the URL. A visit with new params replaces the old
 * set as a whole; the same params again (the quiz keeps ?fbclid= between
 * questions) change nothing. An internal link (utm_source=site) is saved only
 * when nothing is saved, so it never replaces an ad's params.
 */
export const captureAttribution = (search) => {
  if (typeof window === "undefined") return;
  try {
    clearIfExpired();
    const sources = searchParamsFrom(search);
    const incoming = STORAGE_KEYS.reduce((found, key) => {
      found[key] = sources.map((params) => params.get(key)).find(Boolean) || "";
      return found;
    }, emptyAttribution());
    if (!hasAttribution(incoming)) return;

    const stored = readStored();
    if (incoming.utm_source === INTERNAL_SOURCE && hasAttribution(stored)) return;
    const sameVisit = STORAGE_KEYS.every(
      (key) => !incoming[key] || incoming[key] === stored[key],
    );
    if (sameVisit) return;

    clearStored();
    STORAGE_KEYS.forEach((key) => {
      if (incoming[key]) localStorage.setItem(key, incoming[key]);
    });
    localStorage.setItem(SAVED_AT_KEY, String(Date.now()));
  } catch (error) {
    console.log(error);
  }
};

export const readAttribution = () => {
  if (typeof window === "undefined") return emptyAttribution();
  try {
    clearIfExpired();
    return readStored();
  } catch (error) {
    console.log(error);
    return emptyAttribution();
  }
};

export const hasAttribution = (attribution) =>
  !!attribution && STORAGE_KEYS.some((key) => attribution[key]);

// Params from an outside source (ad, newsletter...), not only our own internal
// links. Used to decide whether a store link goes through the OneLink.
export const hasExternalAttribution = (attribution) =>
  hasAttribution(attribution) && attribution.utm_source !== INTERNAL_SOURCE;

export const appDownloadUrl = (childId, attribution = emptyAttribution()) => {
  const params = new URLSearchParams();
  params.set("pid", "facebook");
  if (attribution.fbclid) params.set("af_sub1", attribution.fbclid);
  if (childId) params.set("af_sub2", String(childId));
  ["utm_source", "utm_campaign", "utm_medium", "utm_content"].forEach((key) => {
    if (attribution[key]) params.set(key, attribution[key]);
  });
  return `${ONELINK}?${params.toString()}`;
};
